import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { acquirerToFirestore } from "../models/acquirer";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * AcquirerService
 * Centraliza as operações de banco de dados para o módulo de Adquirentes.
 */
const AcquirerService = {
    /**
     * Busca todas as adquirentes de um tenant.
     */
    getAcquirers: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.ACQUIRERS),
                orderBy("name", "asc")
            );
            const querySnapshot = await getDocs(q);
            const acquirers = [];
            querySnapshot.forEach((doc) => {
                acquirers.push({ id: doc.id, ...doc.data() });
            });
            return acquirers;
        } catch (error) {
            console.error("Erro ao listar adquirentes:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma adquirente.
     */
    save: async (formData, idTenant, authUser, existingAcquirers = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const acquirerData = acquirerToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const acquirerRef = doc(db, "tenants", idTenant, COLLECTIONS.ACQUIRERS, formData.id);
                const oldAcquirer = existingAcquirers.find(a => a.id === formData.id) || {};
                await updateDoc(acquirerRef, acquirerData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldAcquirer, acquirerData);
                if (changes) {
                    AuditService.log({
                        idTenant,
                        user: authUser,
                        module: AUDIT_MODULES.ACQUIRERS,
                        action: AUDIT_ACTIONS.UPDATE,
                        entityId: formData.id,
                        entityName: acquirerData.name,
                        description: `Atualizou a adquirente "${acquirerData.name}"`,
                        details: changes
                    });
                }

                return { id: formData.id, ...acquirerData };
            } else {
                // CREATE
                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.ACQUIRERS), acquirerData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.ACQUIRERS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: acquirerData.name,
                    description: `Criou a adquirente "${acquirerData.name}"`
                });

                return { id: newDocRef.id, ...acquirerData };
            }
        } catch (error) {
            console.error("Erro no AcquirerService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma adquirente.
     */
    delete: async (acquirerId, idTenant, authUser, acquirerName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.ACQUIRERS, acquirerId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.ACQUIRERS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: acquirerId,
                entityName: acquirerName,
                description: `Excluiu a adquirente "${acquirerName || acquirerId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no AcquirerService.delete:", error);
            throw error;
        }
    }
};

export default AcquirerService;
